/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { catalogEntryFor } from './field-catalog';
import { expressionSourceOf } from './mapping-value-slots';

/**
 * The VAST request parameters bidBrain populates **from session context, without the
 * operator authoring a row** — #951 · R5, ratified by Todd 2026-08-07.
 *
 * ## Why this exists
 *
 * OpenRTB has always done this: its body is built procedurally, so `device.ua`,
 * `device.ip`, `device.w/h` and the content fields populate with no operator action. The
 * VAST path did the opposite — it sent **only** what an operator declared, so a source
 * authored without an `ip` row sent a request most exchanges decline to bid on, and
 * nothing anywhere said so. The values were in session context the whole time.
 *
 * The principle this settles, and it is the one this codebase keeps paying for:
 * **a value the proxy has and does not send is a silent loss.** Default to sending it,
 * make the default *visible*, and make turning it off explicit — rather than defaulting to
 * nothing and relying on the operator to know what an exchange needs (#880 omitted keys,
 * #927 an empty harness, #919 pass-through).
 *
 * ## Precedence (R5), and it is the same direction everything else uses
 *
 * ```
 * auto-populated  <  operator row  <  decorator  <  round override
 * ```
 *
 * …with publisher custom replay last of all, so it can never overwrite any of them
 * (#919 · D5). Identical in direction to `BaseDriver.buildRequestHeaders` (#878) and to
 * the `X-Forwarded-For` default (#1006): what the system composes is the floor, and
 * anything the operator or the round decided wins over it.
 *
 * The mechanism is deliberately *not* a new precedence layer — these names seed
 * `getEffectiveParamFieldConfigs`' field set at the bottom, so an operator row of the same
 * name simply replaces the base config and every existing rule applies unchanged.
 * **`suppress` therefore already turns one off**, per source or per round, with no new
 * primitive.
 *
 * ## Why a fixed list rather than "everything we have"
 *
 * Over-sending is not free. A 25-parameter URL risks length limits, and some SSPs treat
 * unrecognised query parameters as key-value targeting — so an unknown extra is not
 * ignored, it can *change* what the exchange thinks is being asked for. This is the set
 * with near-universal agreement on the **key name**, which is the part IAB does not
 * standardise for VAST requests: IAB specifies VAST as a *response* format and standardises
 * a macro vocabulary for *values* (`[DEVICEIP]`, `[IFA]`, …), while every SSP names its own
 * query parameters. Per-SSP renaming belongs in the preset (#949), not here.
 *
 * ## One list, three consumers
 *
 * The runtime (`VastDriver`), the paste-merge (`getStandardFields` in
 * `ad-tag.adapter.ts`) and the console's catalog all read this. They were **two** lists
 * that had already diverged: the paste path omitted `app_bundle` / `app_name` /
 * `app_store_url`, which #960 established as required on a CTV-first platform — so the
 * *dominant* onboarding path was the one missing the fields exchanges price and target on.
 */
export interface AutoPopulatedParam {
  /** The query-parameter name emitted on the wire. */
  name: string;
  /** Operator-facing description — the same copy the catalog and the ghosted row show. */
  description: string;
  /**
   * The macro whose resolution fills it, for display. The driver resolves by **name**
   * through `getMacroValueForFieldName`, so this is documentation rather than a second
   * lookup path — a second one is how `{pod_max_ads}` came to resolve nowhere (#949).
   */
  macro: string;
  /** Context path the value comes from — shown on the row, and used by the parse merge. */
  mappingSource?: string;
  /** `false` only where an exchange effectively cannot bid without it. */
  optional?: boolean;
  /**
   * Resolve from the session **only** — never through a resolver default.
   *
   * `us_privacy` is the case. `resolveContextValue` supplies the neutral `1---` when the
   * session carries no CCPA string (#912), which is right for an operator who *asked* for
   * the field and wrong as something we volunteer: a regulatory position asserted from a
   * blank is exactly what ORTB refuses to send. But dropping a string the publisher
   * **did** send is the silent loss R5 exists to end (Todd, 2026-08-07: *"we can't drop it
   * if not included — 951 should apply to privacy as well"*).
   *
   * So: forward what arrived, invent nothing. That is the whole rule, and this flag is
   * how a field opts out of its own default rather than out of auto-population.
   */
  observedOnly?: boolean;
}

export const AUTO_POPULATED_VAST_PARAMS: ReadonlyArray<AutoPopulatedParam> = [
  {
    name: 'ip',
    description: 'Viewer IP address',
    macro: '{ip}',
    mappingSource: 'session.user.ipAddress',
    optional: false,
  },
  {
    name: 'ua',
    description: 'Device user agent',
    macro: '{ua}',
    mappingSource: 'session.user.userAgent',
    optional: false,
  },
  { name: 'w', description: 'Player width (px)', macro: '{w}', mappingSource: 'session.context.playerWidth' },
  { name: 'h', description: 'Player height (px)', macro: '{h}', mappingSource: 'session.context.playerHeight' },
  {
    name: 'url',
    description: 'Page or app URL the ad runs on',
    macro: '{url}',
    mappingSource: 'session.context.pageUrl',
  },
  // App identity — #960 established these as required on a CTV-first platform:
  // exchanges price, target and filter on them.
  {
    name: 'app_bundle',
    description: 'App bundle ID or domain',
    macro: '{app_bundle}',
    mappingSource: 'session.context.appBundle',
  },
  {
    name: 'app_name',
    description: 'App name as the publisher reports it',
    macro: '{app_name}',
    mappingSource: 'session.context.appName',
  },
  {
    name: 'app_store_url',
    description: 'App store listing URL',
    macro: '{app_store_url}',
    mappingSource: 'session.context.appStoreUrl',
  },
  // Privacy / regulatory. Each is omitted when it cannot be read — the resolver
  // *"omits a flag it cannot read rather than asserting a regulatory position"*, and a
  // wrong `0` says CCPA/GDPR does not apply.
  //
  // `us_privacy` is `observedOnly`: a string the publisher sent is forwarded, and the
  // neutral `1---` is never volunteered. See the note on that flag.
  {
    name: 'us_privacy',
    description: 'US Privacy (CCPA) string',
    macro: '{us_privacy}',
    mappingSource: 'session.user.usPrivacy',
    observedOnly: true,
  },
  { name: 'gdpr', description: 'GDPR-applies flag (0/1)', macro: '{gdpr}', mappingSource: 'session.user.gdpr' },
  {
    name: 'gdpr_consent',
    description: 'GDPR TC string',
    macro: '{gdpr_consent}',
    mappingSource: 'session.user.gdprConsent',
  },
  { name: 'dnt', description: 'Do Not Track flag (0/1)', macro: '{dnt}', mappingSource: 'session.user.dnt' },
  { name: 'lmt', description: 'Limit Ad Tracking flag (0/1)', macro: '{lmt}', mappingSource: 'session.user.lmt' },
  /**
   * **`dur` is the pod's ceiling, not the content's length** (#1095, Todd 2026-08-18:
   * *"on the request it means max_pod_duration"*).
   *
   * It was seeded here against `session.content.contentLength`, so a 2.5-hour film sent
   * `dur=9120` to every VAST demand partner while the pod wanted 150 seconds — a break
   * constraint misstated by two orders of magnitude, on every request, authored by nobody.
   * #1014 had already recorded that the real pod size *"is communicated to the exchange by
   * nothing at all"*; it was worse than nothing.
   */
  {
    name: 'dur',
    description: 'Maximum total pod duration (seconds)',
    macro: '{pod_max_duration}',
    mappingSource: 'pod.maxDuration',
  },
  /**
   * Content length keeps a home, under a name that means it. `content_duration` is the
   * spelling `ad-tag.adapter.ts` already recognises and the one a real request carried in
   * #1014 — it is real pricing signal, and dropping it to fix `dur` would trade one silent
   * loss for another.
   */
  {
    name: 'content_duration',
    description: 'Content length in seconds',
    macro: '{content_duration}',
    mappingSource: 'session.content.contentLength',
  },
  // Resolved per request from the clock, not from session context.
  { name: 'cb', description: 'Cache buster', macro: '{cb}' },
];

/** Just the names — the driver seeds its effective field set from these. */
export const AUTO_POPULATED_VAST_PARAM_NAMES: ReadonlyArray<string> = AUTO_POPULATED_VAST_PARAMS.map((p) => p.name);

/**
 * **The concept a parameter name, macro or path stands for** (`#1018`, moved here `#1251`).
 *
 * Every SSP names its own query parameters and IAB standardises none of them, so *is this concept
 * spoken for* can never be answered by comparing names. `client_ip`, `{ip}` and
 * `session.user.ipAddress` are one concept under three spellings, and this is the normalisation
 * that makes them comparable — delimiters off, camelCase split, lower-cased.
 */
export function canonicalParamConcept(value: string): string {
  return value
    .trim()
    .replace(/^[[{]\{?\s*|\s*\}?[\]}]$/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase();
}

/** The claims one authored row makes about which concept it covers. */
export interface ParamRowConcepts {
  /** The parameter name as authored — the name on the wire. */
  name: string;
  /** `N11f` — the macro token this row resolves from, delimiters as authored. */
  macro?: string;
  /** The context path this row resolves from. */
  mappingSource?: string;
}

/** A row's own `macro` and `mappingSource`, where they say anything. */
function stripEmpty(row: ParamRowConcepts): { macro?: string; mappingSource?: string } {
  return {
    ...(row.macro ? { macro: row.macro } : {}),
    ...(row.mappingSource ? { mappingSource: row.mappingSource } : {}),
  };
}

/**
 * **The concepts one row claims** — its name, its macro, its path — in the normalisation that makes
 * them comparable (`#1018`). The driver's dedupe and {@link displacedAutoParam} both ask it, so the two
 * cannot disagree about which row covers which concept. The source is read from the row's expression
 * too (`X58`), where a row stores it since `#1428`; reading `macro` and `mappingSource` alone is what
 * let a row with a chip cover nothing.
 */
export function paramConceptClaims(row: ParamRowConcepts | ParamRowSources): string[] {
  const source = { ...expressionSourceOf((row as ParamRowSources).expression), ...stripEmpty(row) };
  const claims = [canonicalParamConcept(row.name)];
  if (source.macro) claims.push(canonicalParamConcept(source.macro));
  if (source.mappingSource) claims.push(source.mappingSource);
  return claims;
}

/**
 * **Which authored row covers each concept** — the driver's dedupe (`#1018`), keyed by concept, valued
 * by the covering row's name. **First row wins** where two name one concept.
 *
 * Shared because two readers ask it: the driver, to decide what it sends unprompted, and the console's
 * *Sent automatically* panel, to say so. The panel compared **names** until 2026-09-29, so a `pod` row
 * mapped to `{pod_max_duration}` removed `dur` from the wire and left it listed as sent — a reader
 * narrower than its writer, and a false positive.
 */
export function conceptsCoveredBy(rows: Iterable<ParamRowConcepts | ParamRowSources>): Map<string, string> {
  const covered = new Map<string, string>();
  for (const row of rows) {
    for (const concept of paramConceptClaims(row)) {
      if (!covered.has(concept)) covered.set(concept, row.name);
    }
  }
  return covered;
}

/** The authored row that displaces this auto-populated parameter, given {@link conceptsCoveredBy}'s answer. */
export function coveringRowFor(
  param: { macro?: string; mappingSource?: string },
  covered: ReadonlyMap<string, string>,
): string | undefined {
  return (
    (param.macro ? covered.get(canonicalParamConcept(param.macro)) : undefined) ??
    (param.mappingSource ? covered.get(param.mappingSource) : undefined)
  );
}

/**
 * **Which auto-populated parameter this row displaces, if any** (`X33a`, `#1251`/`#1252`).
 *
 * The driver deduplicates by concept, so an authored `client_ip` correctly removes the
 * auto-populated `ip` — and what it displaces is one of the two things it owes the operator when it
 * resolves to nothing ({@link systemDefaultForParamRow} is the whole answer). Asked on its own where
 * the question really is *displacement*: the console's layer action, where removing a parent's
 * `player_width` gives the automatic `w` back.
 *
 * A console copy would answer by **name** — that is what the mirrored name set could do — and the
 * one case it would get wrong is `client_ip`, which is the case the concept dedupe exists for.
 *
 * **A row covers a concept by any of its three claims**, matching the dedupe: its name, its macro,
 * or its mapping path. Where two rows name one concept the driver gives the displaced value to the
 * first and this answers for both — which is arbitrary, matches what the previous `Set` did, and is
 * a validation finding rather than a resolution question.
 */
export function displacedAutoParam(
  row: ParamRowConcepts | ParamRowSources,
  autoParams: ReadonlyArray<AutoPopulatedParam> = AUTO_POPULATED_VAST_PARAMS,
): AutoPopulatedParam | undefined {
  const claims = new Set<string>(paramConceptClaims(row));

  return autoParams.find(
    (p) =>
      (p.macro !== undefined && claims.has(canonicalParamConcept(p.macro))) ||
      (p.mappingSource !== undefined && claims.has(p.mappingSource)),
  );
}

/**
 * **Where the system's own value comes from, in the operator's terms** — the sentence a surface
 * shows beside *accept the system default*, so it names the value rather than the mechanism
 * (`#1045`). Shared, because the console and the request log must not describe one fall-back
 * differently (*shared logic is not shared words*).
 *
 * Takes the **claims** shape rather than a full {@link AutoPopulatedParam}, because the driver's
 * own `autoPopulatedParams()` returns the slim projection and a description is not needed to say
 * where a value comes from. An `AutoPopulatedParam` satisfies it structurally.
 */
export function describeSystemDefault(param: ParamRowConcepts): string {
  return param.mappingSource ?? param.macro ?? param.name;
}

/** A row's claims about where its value comes from — the three that name a source of its own. */
export interface ParamRowSources extends ParamRowConcepts {
  /** The row's expression, if it resolves through one. Only its presence is read. */
  expression?: unknown;
}

/**
 * **Does this row name a source of its own** — a path, a macro or an expression (`#1425`)?
 *
 * The question that decides which of its two jobs the name-based rescue is doing. Without a source
 * the **name is the mapping**, and the rescue is the resolution. With one that resolved to nothing,
 * the rescue is **the system default by another door**, and answers to *accept the system default*.
 * An expression is a source like the other two: leaving it out is what let a row with a chip skip
 * its system default while the checkbox read ticked (`#1427`).
 */
export function rowNamesItsOwnSource(row: Omit<ParamRowSources, 'name'>): boolean {
  return !!(row.mappingSource || row.macro || row.expression);
}

/** Where a row falls back to when it resolves to nothing, in the driver's terms and the operator's. */
export interface ParamSystemDefault {
  /** The name the driver's name-based resolver is asked for. */
  name: string;
  /** Where that value comes from, in the operator's terms — what the console shows beside the control. */
  from: string;
}

/**
 * **Does this row have a system default, and where from** (`#1427`, ruled (a) by Todd 2026-09-24).
 *
 * One question, two readers, and they must not answer it separately:
 *
 * - the **driver** asks it to decide whether the row may fall back, and to what;
 * - the **console** asks it to decide whether *Accept system default* is offered at all.
 *
 * The console used to ask only {@link displacedAutoParam} — the seventeen parameters sent
 * automatically — while the driver's rescue answered for **any** name the resolver knows, gated by the
 * same flag. So a `genre` row fell back to the session's genre with no control to see or decline it:
 * `X33a`'s false claim, and a reader narrower than its writer. Two answers, in the order the driver
 * tries them:
 *
 * 1. **The row's own name**, where the row names a source of its own and the name is one the resolver
 *    knows. `catalogEntryFor` is that question: `FIELD_CATALOG` is asserted equal to the resolver's
 *    name switch in both directions (`field-catalog-resolution.spec.ts`, `resolvable-macros.test.ts`),
 *    so a name the catalog does not hold is one the rescue does not answer. A row with no source of
 *    its own is excluded, because there the name **is** the mapping and nothing is declined.
 * 2. **The auto-populated parameter it displaces.**
 *
 * Reported as the first that applies. Where both do, the driver tries the second when the first
 * resolves to nothing on a given request, which the request log then names.
 */
export function systemDefaultForParamRow(
  row: ParamRowSources,
  autoParams: ReadonlyArray<AutoPopulatedParam> = AUTO_POPULATED_VAST_PARAMS,
): ParamSystemDefault | undefined {
  const byName = nameRescueFor(row);
  if (byName) return byName;
  const displaced = displacedAutoParam(row, autoParams);
  return displaced ? { name: displaced.name, from: describeSystemDefault(displaced) } : undefined;
}

/**
 * The first half of {@link systemDefaultForParamRow} on its own: the name-based rescue, where it is a
 * system default. The driver reaches the displaced parameter through its own dedupe (which gives it
 * to the first of two covering rows), so it asks this half by name.
 */
export function nameRescueFor(row: ParamRowSources): ParamSystemDefault | undefined {
  if (!rowNamesItsOwnSource(row)) return undefined;
  const entry = catalogEntryFor(row.name);
  if (!entry) return undefined;
  return { name: row.name, from: entry.path ?? `{${entry.name}}` };
}
